import React, { useRef, useCallback, useState, useEffect } from 'react';
import { GoogleMap, Libraries, useLoadScript } from '@react-google-maps/api';
import { setLocalStorage } from '../../utils/helper';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { updateLocation } from '../../redux/actions/locationSlice';
import { RootState } from '../../redux/store/store';
import { Autocomplete } from '@react-google-maps/api';
import Button from '../button/Button';
import { useApiServices } from '../../services/endpoints/ApiEndpoint';

const center = {
    lat: 13.7563,
    lng: 100.5018,
};

const libraries: Libraries = ['places'];

interface Address {
    id: string
    name: string
    addressLine1: string
    addressLine2: string
    latitude: number
    longitude: number
    postalCode: string
    contact: {
        name: string
        phone: string
    }
    createDate: string
}

export const MapSelector: React.FC = () => {
    const location = useSelector((state: RootState) => state.location)
    const user = useSelector((state: RootState) => state.user)
    const dispatch = useDispatch()
    const navigate = useNavigate();
    const api = useApiServices()
    
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: 'AIzaSyDarpg8lcEx40Wu37gDXHaz6dTMyNFOXDc',
        libraries,
    });

    const mapRef = useRef<google.maps.Map | null>(null);
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const [selectedPosition, setSelectedPosition] = useState(center);
    const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
    const [addressList, setAddressList] = useState<Address[]>([])
    const [showAddressDropdown, setShowAddressDropdown] = useState(false)

    const onLoad = useCallback((map: google.maps.Map) => {
        mapRef.current = map;
    }, []);

    const onIdle = useCallback(() => {
        if (mapRef.current) {
            const center = mapRef.current.getCenter();
            if (center) {
                setSelectedPosition({
                    lat: center.lat(),
                    lng: center.lng(),
                });
            }
        }
    }, []);

    const handleZoomIn = () => {
        if (mapRef.current) {
            const currentZoom = mapRef.current.getZoom() || 15;
            mapRef.current.setZoom(currentZoom + 1);
        }
    };

    const handleZoomOut = () => {
        if (mapRef.current) {
            const currentZoom = mapRef.current.getZoom() || 15;
            mapRef.current.setZoom(currentZoom - 1);
        }
    };

    const onLoadAutocomplete = (auto: google.maps.places.Autocomplete) => {
        setAutocomplete(auto);
    };

    const onPlaceChanged = () => {
        if (autocomplete) {
            const place = autocomplete.getPlace();
            if (place && place.geometry && place.geometry.location) {
                const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();
                setSelectedPosition({ lat, lng });
                if (mapRef.current) {
                    mapRef.current.panTo({ lat, lng });
                }
            }
        }
    };

    const onClearSearch = () => {
        if (autocomplete) {
            autocomplete.set('place', null);
        }
        if (searchInputRef.current) {
            searchInputRef.current.value = '';
        }
    };

    useEffect(() => {
        if (location) {
            console.log('set center',location);
            
            setSelectedPosition({lat:location.lat,lng:location.lng});
        }
    }, [location]);

    useEffect(() => {
        const fetchAddresses = async () => {
            if (!user?.entityUniqueIdentifierId) return

            try {
                const { isSuccess, result } = await api.GetAddressesAsync(user.entityUniqueIdentifierId)
                if (isSuccess && Array.isArray(result)) {
                    // Get last 5 addresses (most recent first)
                    const last5 = result
                        .sort((a: any, b: any) => new Date(b.createDate).getTime() - new Date(a.createDate).getTime())
                        .slice(0, 5)
                    setAddressList(last5)
                }
            } catch (error) {
                console.error('Error fetching addresses:', error)
            }
        }

        fetchAddresses()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.entityUniqueIdentifierId]);

    const handleSelectAddress = (address: Address) => {
        setSelectedPosition({ lat: address.latitude, lng: address.longitude })
        if (mapRef.current) {
            mapRef.current.panTo({ lat: address.latitude, lng: address.longitude })
        }
        setShowAddressDropdown(false)
    }

    if (!isLoaded) return <div className="flex items-center justify-center h-full text-body-lg">Loading...</div>;

    return (
        <div className="w-full h-full absolute">
            <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={selectedPosition}
                zoom={15}
                onLoad={onLoad}
                onIdle={onIdle}
                options={{
                    disableDefaultUI: true,
                    gestureHandling: 'greedy',
                }}
            />
            <div
                className="absolute top-[50%] left-[50%] w-[32px] h-[32px] bg-contain bg-no-repeat pointer-events-none"
                style={{
                    backgroundImage: 'url(../../assets/icons/PinIcon.svg)',
                    transform: 'translate(-50%, -100%)',
                }}
            ></div>
            
            {/* Address History Dropdown */}
            {addressList.length > 0 && (
                <div className="absolute top-[10px] left-[10px] z-[10] w-[calc(100%-20px)] box-border">
                    <Button
                        onClick={() => setShowAddressDropdown(!showAddressDropdown)}
                        variant="secondary"
                        size="sm"
                        className="w-full flex justify-between items-center"
                    >
                        <span>📍 ประวัติที่อยู่ ({addressList.length})</span>
                        <span>{showAddressDropdown ? '▲' : '▼'}</span>
                    </Button>
                    
                    {showAddressDropdown && (
                        <div className="mt-2 bg-white rounded-md shadow-lg max-h-[300px] overflow-y-auto border border-gray-200">
                            {addressList.map((address) => (
                                <div
                                    key={address.id}
                                    onClick={() => handleSelectAddress(address)}
                                    className="p-3 border-b border-gray-200 hover:bg-gray-100 cursor-pointer transition-colors last:border-b-0"
                                >
                                    <div className="font-bold text-sm">{address.contact?.name || address.name}</div>
                                    <div className="text-xs text-gray-600 mt-1">
                                        {address.addressLine1}
                                        {address.addressLine2 && `, ${address.addressLine2}`}
                                        {address.postalCode && ` ${address.postalCode}`}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div className={`absolute ${addressList.length > 0 ? 'top-[60px]' : 'top-[10px]'} left-[10px] z-[1] flex items-center gap-[10px] flex-nowrap w-[calc(100%-20px)] box-border`}>
                <div className="flex-grow min-w-0">
                    <Autocomplete onLoad={onLoadAutocomplete} onPlaceChanged={onPlaceChanged}>
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Search location"
                            className="w-full h-[40px] px-3 rounded-md border border-border-default box-border"
                        />
                    </Autocomplete>
                </div>
                <Button
                    onClick={onClearSearch}
                    variant="secondary"
                    size="sm"
                    className="flex-shrink-0 whitespace-nowrap"
                >
                    ล้าง
                </Button>
                <Button
                    onClick={() => {
                        setLocalStorage('location', { lat: selectedPosition.lat, lng: selectedPosition.lng });
                        dispatch(updateLocation({ lat: selectedPosition.lat, lng: selectedPosition.lng, isDefault: false }));
                        navigate('/');
                    }}
                    variant="primary"
                    size="sm"
                    className="flex-shrink-0 whitespace-nowrap"
                >
                    ตกลง
                </Button>
            </div>

            <div className={`absolute right-[10px] ${addressList.length > 0 ? 'top-[110px]' : 'top-[60px]'} flex flex-col gap-1 z-[1]`}>
                <Button
                    onClick={handleZoomIn}
                    variant="icon"
                    className="w-[40px] h-[40px] min-h-[40px] text-[1.25rem] font-bold shadow-md"
                >
                    +
                </Button>
                <Button
                    onClick={handleZoomOut}
                    variant="icon"
                    className="w-[40px] h-[40px] min-h-[40px] text-[1.25rem] font-bold shadow-md"
                >
                    -
                </Button>
            </div>
        </div>
    );
};
